import { useIntl } from 'react-intl';

import { DeviationValue } from '#components/shared/DeviationValue.tsx';
import LabeledValue from '#components/shared/LabeledValue.tsx';
import type { HandlingUnit } from '#domain/model/Goods.ts';
import { hasDeviation } from '#domain/model/hasDeviation.ts';
import { cargoPackagingTranslationKeys } from '#domain/model/packagingOptions.ts';

type TourServiceHandlingUnitDetailsProps = {
    unit: HandlingUnit;
};

const sectionClasses = 'display-flex flex-column gap-10 bg-lightest padding-10 rounded';
const columnClasses = 'display-grid gap-10';

const isValidValue = (value: number | undefined) => value !== undefined && value > 0;

export const HandlingUnitDetails = ({ unit }: TourServiceHandlingUnitDetailsProps) => {
    const intl = useIntl();

    const packagingLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.common.load-carrier',
    });

    const hasLoadedQuantityDeviation = hasDeviation(unit.quantity.target, unit.quantity.loaded);
    const hasUnloadedQuantityDeviation = hasDeviation(unit.quantity.target, unit.quantity.unloaded);
    const hasLoadedWeightDeviation = hasDeviation(unit.weight?.target, unit.weight?.loaded);
    const hasUnloadedWeightDeviation = hasDeviation(unit.weight?.target, unit.weight?.unloaded);

    const hasLoadedLoadingMeterDeviation = hasDeviation(unit.loadingMeter?.target, unit.loadingMeter?.loaded);
    const hasUnloadedLoadingMeterDeviation = hasDeviation(unit.loadingMeter?.target, unit.loadingMeter?.unloaded);

    const emptyPlaceholder = '-';
    const targetQuantity = unit.quantity.target;
    const loadedQuantity = unit.quantity.loaded;
    const unloadedQuantity = unit.quantity.unloaded;
    const hasQuantityValues =
        isValidValue(targetQuantity) || isValidValue(loadedQuantity) || isValidValue(unloadedQuantity);
    const loadedWeight = unit.weight?.loaded;
    const unloadedWeight = unit.weight?.unloaded;
    const targetWeight = unit.weight?.target;
    const hasWeightValues = isValidValue(targetWeight) || isValidValue(loadedWeight) || isValidValue(unloadedWeight);
    const loadedLoadingMeter = unit.loadingMeter?.loaded;
    const unloadedLoadingMeter = unit.loadingMeter?.unloaded;
    const targetLoadingMeter = unit.loadingMeter?.target;
    const hasLoadingMeterValues =
        isValidValue(targetLoadingMeter) || isValidValue(loadedLoadingMeter) || isValidValue(unloadedLoadingMeter);
    const hasDimensions = isValidValue(unit.length) || isValidValue(unit.height) || isValidValue(unit.width);

    return (
        <div data-testid={`order-stop-form-${unit.id}`} className={sectionClasses}>
            <div className={`${columnClasses} grid-cols-2`}>
                {unit.articles.map(article => (
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.cargo',
                        })}
                        key={article.article_id ?? article.customer_article_id}
                        value={article.name}
                    />
                ))}
                <LabeledValue
                    boxed
                    value={intl.formatMessage({
                        id:
                            unit.packageType in cargoPackagingTranslationKeys
                                ? cargoPackagingTranslationKeys[
                                      unit.packageType as keyof typeof cargoPackagingTranslationKeys
                                  ]
                                : cargoPackagingTranslationKeys.unspecified,
                    })}
                    label={packagingLabel}
                />
            </div>
            {hasQuantityValues && (
                <div className={`${columnClasses} grid-cols-3`}>
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.target-quantity',
                        })}
                        value={targetQuantity}
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.quantity-loaded',
                        })}
                        value={
                            hasLoadedQuantityDeviation ? (
                                <DeviationValue
                                    targetQuantity={targetQuantity}
                                    actualQuantity={loadedQuantity}
                                    emptyPlaceholder={emptyPlaceholder}
                                />
                            ) : loadedQuantity !== undefined ? (
                                loadedQuantity
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.quantity-unloaded',
                        })}
                        value={
                            hasUnloadedQuantityDeviation ? (
                                <DeviationValue
                                    targetQuantity={targetQuantity}
                                    actualQuantity={unloadedQuantity}
                                    emptyPlaceholder={emptyPlaceholder}
                                />
                            ) : unloadedQuantity !== undefined ? (
                                unloadedQuantity
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                </div>
            )}
            {hasWeightValues && (
                <div className={`${columnClasses} grid-cols-3`}>
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.target-weight',
                        })}
                        value={targetWeight !== undefined ? `${targetWeight} kg` : emptyPlaceholder}
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.weight-loaded',
                        })}
                        value={
                            hasLoadedWeightDeviation && targetWeight !== undefined ? (
                                <DeviationValue
                                    targetQuantity={targetWeight}
                                    actualQuantity={loadedWeight}
                                    emptyPlaceholder={emptyPlaceholder}
                                    unit='kg'
                                />
                            ) : loadedWeight !== undefined ? (
                                `${loadedWeight} kg`
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.weight-unloaded',
                        })}
                        value={
                            hasUnloadedWeightDeviation && targetWeight !== undefined ? (
                                <DeviationValue
                                    targetQuantity={targetWeight}
                                    actualQuantity={unloadedWeight}
                                    emptyPlaceholder={emptyPlaceholder}
                                    unit='kg'
                                />
                            ) : unloadedWeight !== undefined ? (
                                `${unloadedWeight} kg`
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                </div>
            )}
            {hasLoadingMeterValues && (
                <div className={`${columnClasses} grid-cols-3`}>
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.target-loading-meter',
                        })}
                        value={unit.loadingMeter?.target ? `${unit.loadingMeter.target} Ldm` : emptyPlaceholder}
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.loading-meter-loaded',
                        })}
                        value={
                            hasLoadedLoadingMeterDeviation && targetLoadingMeter !== undefined ? (
                                <DeviationValue
                                    targetQuantity={targetLoadingMeter}
                                    actualQuantity={loadedLoadingMeter}
                                    emptyPlaceholder={emptyPlaceholder}
                                    unit='Ldm'
                                />
                            ) : loadedLoadingMeter !== undefined ? (
                                `${loadedLoadingMeter} Ldm`
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.loading-meter-unloaded',
                        })}
                        value={
                            hasUnloadedLoadingMeterDeviation && targetLoadingMeter !== undefined ? (
                                <DeviationValue
                                    targetQuantity={targetLoadingMeter}
                                    actualQuantity={unloadedLoadingMeter}
                                    emptyPlaceholder={emptyPlaceholder}
                                    unit='Ldm'
                                />
                            ) : unloadedLoadingMeter !== undefined ? (
                                `${unloadedLoadingMeter} Ldm`
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                </div>
            )}
            {hasDimensions && (
                <div className={`${columnClasses} grid-cols-3`}>
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.length',
                        })}
                        value={unit.length ?? emptyPlaceholder}
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.height',
                        })}
                        value={unit.height ?? emptyPlaceholder}
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.width',
                        })}
                        value={unit.width ?? emptyPlaceholder}
                    />
                </div>
            )}
            {unit.comments.loading ||
                (unit.comments.unloading && (
                    <div className={`${columnClasses} grid-cols-1`}>
                        {unit.comments.loading && (
                            <LabeledValue
                                label={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.comment.driver.loading',
                                })}
                                className='padding-x-10 padding-y-5'
                                icon='comment'
                                value={unit.comments.loading ?? emptyPlaceholder}
                            />
                        )}
                        {unit.comments.unloading && (
                            <LabeledValue
                                label={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.comment.driver.unloading',
                                })}
                                className='padding-x-10 padding-y-5'
                                icon='comment'
                                value={unit.comments.unloading ?? emptyPlaceholder}
                            />
                        )}
                    </div>
                ))}
        </div>
    );
};
